import { type Despatch, DESPATCH_FLOW_LABELS, RECEIPT_ADVICE_STATUS_LABELS } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  FormField,
  Select,
  SkeletonRows,
  Textarea,
  useToast,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, Download, FileCode2, RefreshCw, Send } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import {
  ReasonDialog,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { useDespatch } from "../../../../features/invoicing/queries.js";
import {
  downloadDocumentFile,
  EdocumentStatusBadge,
  Field,
  InvoiceStatusBadge,
  InvoicingPage,
  ReceiptAdviceBadge,
  Section,
  useInvoicingCrumb,
  useInvoicingPermissions,
  useRefreshInvoicing,
} from "../../../../features/invoicing/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/faturalar/irsaliyeler/$id")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: DespatchDetailPage,
});

function DespatchDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInvoicingCrumb("/faturalar/irsaliyeler");
  const detail = useDespatch(id);
  const despatch = detail.data;
  const title = despatch?.despatchNo ?? t("invoicing.detail.draftTitle");

  return (
    <InvoicingPage
      title={title}
      {...(despatch ? { description: DESPATCH_FLOW_LABELS[despatch.flow] } : {})}
      breadcrumb={[crumb, { label: title }]}
      actions={despatch ? <DespatchActions despatch={despatch} /> : null}
    >
      {() =>
        detail.error ? (
          <Alert tone="danger">{errorMessage(detail.error)}</Alert>
        ) : !despatch ? (
          <SkeletonRows rows={8} />
        ) : (
          <DespatchBody despatch={despatch} />
        )
      }
    </InvoicingPage>
  );
}

function DespatchActions({ despatch }: { despatch: Despatch }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const [busy, setBusy] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [adviceOpen, setAdviceOpen] = useState(false);

  async function run(kind: string, action: () => Promise<void>): Promise<void> {
    setBusy(kind);
    try {
      await action();
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  const canManage = permissions.canManageDespatch;
  const isDraft = despatch.status === "DRAFT";

  return (
    <>
      {despatch.status === "ISSUED" && permissions.canSend && despatch.flow === "OUTGOING" ? (
        <Button
          variant="outline"
          loading={busy === "send"}
          onClick={() =>
            void run("send", async () => {
              await api.POST("/despatch-advices/{id}/send", {
                params: { path: { id: despatch.id } },
                body: { version: despatch.version },
              });
              await refresh(despatch.companyId);
              toast({ tone: "success", title: t("invoicing.list.resendQueued") });
            })
          }
        >
          <Send aria-hidden="true" />
          {t("invoicing.actions.resend")}
        </Button>
      ) : null}
      {despatch.status === "ISSUED" && !["NOT_APPLICABLE", "CANCELLED"].includes(despatch.edocumentStatus) ? (
        <Button
          variant="outline"
          loading={busy === "refresh"}
          onClick={() =>
            void run("refresh", async () => {
              await api.POST("/despatch-advices/{id}/refresh-status", {
                params: { path: { id: despatch.id } },
              });
              await refresh(despatch.companyId);
              toast({ tone: "success", title: t("invoicing.detail.statusRefreshed") });
            })
          }
        >
          <RefreshCw aria-hidden="true" />
          {t("invoicing.actions.refreshStatus")}
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" loading={busy === "pdf" || busy === "xml"}>
            {t("common.actions")}
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("invoicing.detail.documentActions")}</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() =>
              void run("pdf", async () => {
                const { data } = await api.GET("/despatch-advices/{id}/pdf", {
                  params: { path: { id: despatch.id } },
                });
                downloadDocumentFile(data!);
              })
            }
          >
            <Download aria-hidden="true" />
            {t("invoicing.actions.pdf")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              void run("xml", async () => {
                const { data } = await api.GET("/despatch-advices/{id}/xml", {
                  params: { path: { id: despatch.id } },
                });
                downloadDocumentFile(data!);
              })
            }
          >
            <FileCode2 aria-hidden="true" />
            {t("invoicing.actions.xml")}
          </DropdownMenuItem>
          {despatch.flow === "INCOMING" && despatch.status === "ISSUED" && canManage ? (
            <DropdownMenuItem onSelect={() => setAdviceOpen(true)}>
              {t("invoicing.despatch.receiptAdvice")}
            </DropdownMenuItem>
          ) : null}
          {despatch.status !== "CANCELLED" && canManage ? (
            <DropdownMenuItem onSelect={() => setCancelOpen(true)}>
              {t("invoicing.actions.cancel")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {isDraft && canManage ? (
        <Button
          loading={busy === "issue"}
          onClick={() =>
            void run("issue", async () => {
              await api.POST("/despatch-advices/{id}/issue", {
                params: { path: { id: despatch.id } },
                body: { version: despatch.version },
              });
              await refresh(despatch.companyId);
              toast({ tone: "success", title: t("invoicing.despatch.issued") });
            })
          }
        >
          {t("invoicing.actions.issue")}
        </Button>
      ) : null}

      <ReasonDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t("invoicing.despatch.cancelTitle")}
        description={t("invoicing.despatch.cancelDescription")}
        confirmLabel={t("invoicing.actions.cancel")}
        onConfirm={async (reason) => {
          await api.POST("/despatch-advices/{id}/cancel", {
            params: { path: { id: despatch.id } },
            body: { reason, version: despatch.version },
          });
          await refresh(despatch.companyId);
          toast({ tone: "success", title: t("invoicing.detail.cancelled") });
        }}
      />
      <ReceiptAdviceDialog open={adviceOpen} onOpenChange={setAdviceOpen} despatch={despatch} />
    </>
  );
}

/** Gelen irsaliyeye teslim alma yanıtı: teslim alındı / kısmen / red + not. */
function ReceiptAdviceDialog({
  open,
  onOpenChange,
  despatch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  despatch: Despatch;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const [status, setStatus] = useState<"ACCEPTED" | "PARTIAL" | "REJECTED">("ACCEPTED");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("invoicing.despatch.receiptAdviceTitle")}
      description={t("invoicing.despatch.receiptAdviceDescription")}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          api
            .POST("/despatch-advices/{id}/receipt-advice", {
              params: { path: { id: despatch.id } },
              body: { status, note: note.trim() || null, version: despatch.version },
            })
            .then(async () => {
              await refresh(despatch.companyId);
              toast({ tone: "success", title: t("common.saved") });
              onOpenChange(false);
            })
            .catch((caught: unknown) => setError(errorMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("common.status")} required>
          {(control) => (
            <Select
              {...control}
              value={status}
              onChange={(event) => setStatus(event.target.value as typeof status)}
            >
              {(["ACCEPTED", "PARTIAL", "REJECTED"] as const).map((option) => (
                <option key={option} value={option}>
                  {RECEIPT_ADVICE_STATUS_LABELS[option]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("invoicing.fields.notes")}>
          {(control) => (
            <Textarea
              {...control}
              rows={3}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          )}
        </FormField>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function DespatchBody({ despatch }: { despatch: Despatch }): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4">
      {despatch.status === "CANCELLED" ? (
        <Alert tone="warning" title={t("invoicing.detail.cancelledTitle")}>
          {despatch.cancelReason ?? ""}
        </Alert>
      ) : null}
      {despatch.edocumentError ? (
        <Alert tone="danger" title={t("invoicing.detail.edocumentErrorTitle")}>
          {despatch.edocumentError}
        </Alert>
      ) : null}
      <Section title={t("invoicing.detail.identity")}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("common.status")}>
            <span className="flex flex-wrap gap-1.5">
              <InvoiceStatusBadge status={despatch.status} />
              <EdocumentStatusBadge status={despatch.edocumentStatus} error={despatch.edocumentError} />
              <ReceiptAdviceBadge status={despatch.receiptAdviceStatus} />
            </span>
          </Field>
          <Field label={t("invoicing.fields.counterparty")}>
            <Link
              to="/cari/$id"
              params={{ id: despatch.counterpartyId }}
              className="text-primary underline-offset-4 hover:underline"
            >
              {despatch.counterpartyName}
            </Link>
          </Field>
          <Field label={t("invoicing.fields.issueDate")}>
            <span className="tabular">
              {formatDate(despatch.issueDate)} {despatch.issueTime.slice(0, 5)}
            </span>
          </Field>
          <Field label={t("invoicing.despatch.shipmentDate")}>
            <span className="tabular">{formatDateTime(despatch.shipmentAt)}</span>
          </Field>
          <Field label={t("invoicing.fields.ettn")}>
            <span className="font-mono text-xs">{despatch.ettn}</span>
          </Field>
          <Field label={t("invoicing.despatch.vehiclePlate")}>{despatch.vehiclePlate ?? "—"}</Field>
          <Field label={t("invoicing.despatch.driverName")}>{despatch.driverName ?? "—"}</Field>
          <Field label={t("invoicing.despatch.carrierName")}>{despatch.carrierName ?? "—"}</Field>
          {despatch.stationName ? (
            <Field label={t("invoicing.fields.station")}>{despatch.stationName}</Field>
          ) : null}
          {despatch.deliveryAddress ? (
            <Field label={t("invoicing.despatch.deliveryAddress")}>{despatch.deliveryAddress}</Field>
          ) : null}
          {despatch.notes ? <Field label={t("invoicing.fields.notes")}>{despatch.notes}</Field> : null}
        </dl>
        {despatch.invoices.length > 0 ? (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            {despatch.invoices.map((invoice) => (
              <Button asChild variant="outline" size="sm" key={invoice.id}>
                <Link to="/faturalar/$id" params={{ id: invoice.id }}>
                  {invoice.invoiceNo ?? t("invoicing.despatch.invoiceLink")}
                </Link>
              </Button>
            ))}
          </div>
        ) : null}
      </Section>

      <Section title={t("invoicing.detail.linesTitle")}>
        <SimpleTable
          caption={t("invoicing.despatch.linesCaption")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                #
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.fields.description")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.quantity")}
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.fields.unit")}
              </th>
            </tr>
          }
        >
          {despatch.lines.map((line) => (
            <tr key={line.id} className="border-t">
              <td className={tdClass}>{line.lineNo}</td>
              <td className={tdClass}>
                {line.productCode ? (
                  <span className="font-mono text-xs text-muted-foreground">{line.productCode} </span>
                ) : null}
                {line.description}
              </td>
              <td className={tdRightClass}>{Decimal.from(line.quantity).formatTr(3)}</td>
              <td className={tdClass}>{line.unitCode}</td>
            </tr>
          ))}
        </SimpleTable>
      </Section>
    </div>
  );
}
